import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { api, ApiError } from '@/lib/api';
import type { User } from '@fixtyer/shared';

const API_URL = import.meta.env.VITE_API_URL || '';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  // Public pages are prerendered signed-out at build time. The persisted
  // auth store rehydrates synchronously on the client, so exposing `user`
  // during the first render would make hydration mismatch for logged-in
  // visitors. Gate it behind a post-mount flag instead.
  const [mounted, setMounted] = useState(false);
  const { user, setUser, clearAuth, accessToken } = useAuthStore();

  useEffect(() => {
    setMounted(true);
    const initAuth = async () => {
      // Read from the store directly so a token refresh mid-flight doesn't
      // restart this effect with a stale closure value.
      if (useAuthStore.getState().accessToken) {
        try {
          // The api client handles 401 -> refresh -> retry automatically, so a
          // stale access token with a valid refresh token still boots the session.
          const data = await api.get<{ user: User }>('/api/auth/me');
          setUser(data.user);
        } catch (error) {
          // Only clear auth when the session is genuinely invalid (refresh
          // failed or /me still rejects after refresh). Network blips keep
          // the persisted tokens so the next request can retry.
          if (error instanceof ApiError && error.status === 401) {
            clearAuth();
          }
        }
      }
      setIsLoading(false);
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    const data = await response.json();
    useAuthStore.getState().setAuth(data.user, data.accessToken, data.refreshToken);
  };

  const register = async (email: string, password: string, name: string) => {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Registration failed');
    }

    const data = await response.json();
    useAuthStore.getState().setAuth(data.user, data.accessToken, data.refreshToken);
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    } finally {
      clearAuth();
    }
  };

  const visibleUser = mounted ? user : null;

  return (
    <AuthContext.Provider
      value={{
        user: visibleUser,
        isLoading,
        isAuthenticated: !!visibleUser,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
